import { useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import type { DemoReadinessCheck, DemoReadinessFix, DemoReadinessStatus } from '../../../services/ownerDemoLaneService'
import { navigateOwner } from '../ownerRouter'
import { useDemoReadiness, useDemoWorkspaces } from './useDemoReadiness'

const CHECK_TITLES: Record<string, NlsKey> = {
  'demo-unlock': 'NLSOwnerConsoleDemoLaneReadyCheckUnlock',
  wallet: 'NLSOwnerConsoleDemoLaneReadyCheckWallet',
  'story-saved': 'NLSOwnerConsoleDemoLaneReadyCheckStorySaved',
  'story-restored': 'NLSOwnerConsoleDemoLaneReadyCheckStoryRestored',
  'release-sync': 'NLSOwnerConsoleDemoLaneReadyCheckReleaseSync',
  freeze: 'NLSOwnerConsoleDemoLaneReadyCheckFreeze',
  maintenance: 'NLSOwnerConsoleDemoLaneReadyCheckMaintenance',
}

const FIX_LABELS: Record<DemoReadinessFix, NlsKey> = {
  TURN_ON_UNLOCK: 'NLSOwnerConsoleDemoLaneReadyFixTurnOnUnlock',
  RESTORE_STORY: 'NLSOwnerConsoleDemoLaneReadyFixRestoreStory',
  GRANT_CREDITS: 'NLSOwnerConsoleDemoLaneReadyFixGrantCredits',
}

const STATUS_LABELS: Record<DemoReadinessStatus, NlsKey> = {
  green: 'NLSOwnerConsoleDemoLaneReadyStatusGreen',
  amber: 'NLSOwnerConsoleDemoLaneReadyStatusAmber',
  red: 'NLSOwnerConsoleDemoLaneReadyStatusRed',
}

const OVERALL_LABELS: Record<DemoReadinessStatus, NlsKey> = {
  green: 'NLSOwnerConsoleDemoLaneReadyOverallGreen',
  amber: 'NLSOwnerConsoleDemoLaneReadyOverallAmber',
  red: 'NLSOwnerConsoleDemoLaneReadyOverallRed',
}

// The three fixes live on the workspace's Config tab (demo unlock switch, saved stories, generic action list with Grant credits).
// They navigate there rather than run inline: each needs a preview, a typed workspace name or a reason that the detail page already owns.
const openWorkspaceConfig = (workspaceId: string) =>
  navigateOwner({ view: 'workspace-detail', workspaceId, query: { tab: 'config' } })

const worst = (checks: DemoReadinessCheck[]): DemoReadinessStatus =>
  checks.some((check) => check.status === 'red') ? 'red' : checks.some((check) => check.status === 'amber') ? 'amber' : 'green'

/** "Ready to demo" card (R35 DL5.b): overall rollup, each check, one-click fix navigation. */
export function DemoReadyCard({ refreshToken }: { refreshToken: number }) {
  const { t } = useNls()
  const { workspaces, loading: workspacesLoading, error: workspacesError } = useDemoWorkspaces(true)
  const [picked, setPicked] = useState<string | null>(null)
  const selected = workspaces && workspaces.length > 0
    ? (workspaces.find((workspace) => workspace.id === picked) ?? workspaces[0])
    : null
  const readiness = useDemoReadiness(selected?.id ?? null, refreshToken)
  const checks = readiness.data?.checks ?? []
  const overall = readiness.data ? worst(checks) : null

  return (
    <section className="ownerDemoLaneSection ownerDemoReady" aria-label={t('NLSOwnerConsoleDemoLaneReadyTitle')} data-testid="demo-ready-card">
      <div className="ownerPanelHeader">
        <div>
          <h3>{t('NLSOwnerConsoleDemoLaneReadyTitle')}</h3>
          <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneReadyHint')}</p>
        </div>
        <button
          type="button"
          className="ownerModalBtn ownerModalBtn--default"
          onClick={readiness.reload}
          disabled={!selected || readiness.loading}
        >
          {t('NLSOwnerConsoleDemoLaneRefresh')}
        </button>
      </div>

      {workspacesLoading ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneReadyChecking')}</p> : null}
      {workspacesError ? (
        <div role="alert">
          <strong>{t('NLSOwnerConsoleDemoLaneReadyWorkspacesFailed')}</strong>
          <p className="ownerDemoLaneError">{workspacesError}</p>
        </div>
      ) : null}

      {workspaces && workspaces.length === 0 ? (
        <div>
          <strong>{t('NLSOwnerConsoleDemoLaneReadyNoWorkspace')}</strong>
          <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneReadyNoWorkspaceBody')}</p>
          <button type="button" className="ownerModalBtn ownerModalBtn--default" onClick={() => navigateOwner({ view: 'workspaces' })}>
            {t('NLSOwnerConsoleDemoLaneReadyOpenWorkspaces')}
          </button>
        </div>
      ) : null}

      {workspaces && selected ? (
        <>
          {workspaces.length > 1 ? (
            <label className="ownerDemoReadyPicker">
              <span>{t('NLSOwnerConsoleDemoLaneReadyWorkspace')}</span>
              <select value={selected.id} onChange={(event) => setPicked(event.target.value)}>
                {workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}
              </select>
            </label>
          ) : (
            <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneReadyWorkspaceNamed', { name: selected.name })}</p>
          )}

          {readiness.loading ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneReadyChecking')}</p> : null}
          {readiness.error ? (
            <div role="alert">
              <strong>{t('NLSOwnerConsoleDemoLaneReadyLoadFailed')}</strong>
              <p className="ownerDemoLaneError">{readiness.error}</p>
            </div>
          ) : null}

          {overall ? (
            <p>
              <span className={`ownerDemoReadyBadge ownerDemoReadyBadge--${overall}`} data-status={overall} data-testid="demo-ready-overall">
                {t(OVERALL_LABELS[overall])}
              </span>
            </p>
          ) : null}

          {checks.length > 0 ? (
            <ul className="ownerDemoReadyChecks">
              {checks.map((check) => (
                <li key={check.id} data-check={check.id} data-status={check.status}>
                  <span className={`ownerDemoReadyBadge ownerDemoReadyBadge--${check.status}`}>{t(STATUS_LABELS[check.status])}</span>
                  <strong>{CHECK_TITLES[check.id] ? t(CHECK_TITLES[check.id]) : check.id}</strong>
                  <span className="ownerDemoReadyDetail">{check.detail}</span>
                  {check.fix ? (
                    <button
                      type="button"
                      className="ownerModalBtn ownerModalBtn--default"
                      onClick={() => openWorkspaceConfig(selected.id)}
                    >
                      {t(FIX_LABELS[check.fix])}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
